import { useQuery, useLazyQuery, useMutation } from "@apollo/client";
import {
  GET_GOTO_PHONE_NUMBERS,
  GET_GOTO_CALL_EXTENSIONS,
  GET_GOTO_USERS,
  GET_GOTO_DEVICES,
  GOTO_DASHBOARD_SUMMARY,
} from "../graphql/query";
import {
  UPDATE_GOTO_CALL_EXTENSION,
  UPDATE_GOTO_PHONE_NUMBER,
  UPDATE_GOTO_USER,
  UPDATE_GOTO_CALL_DEVICE,
  SYNC_GOTO_CALL_DATA,
} from "../graphql/mutation/mutation";

/**
 * Scalable GoTo Integration Hook
 *
 * This hook provides a centralized way to interact with GoTo integration APIs.
 * It supports queries and mutations for GoTo phone numbers, call extensions, and related operations.
 *
 * @param {Object} config - Configuration object
 * @param {string} config.operation - The operation to perform (e.g., 'getPhoneNumbers', 'getCallExtensions')
 * @param {Object} config.variables - Variables for the query/mutation
 * @param {boolean} config.skip - Skip the query execution (default: false)
 * @param {string} config.fetchPolicy - Apollo fetch policy (default: 'cache-first')
 * @param {boolean} config.lazy - Whether to use lazy query (default: false)
 * @param {Function} config.onCompleted - Callback function when query/mutation completes
 * @param {Function} config.onError - Callback function when query/mutation errors
 *
 * @returns {Object} Object containing data, loading state, error, and operation functions
 *
 * @example
 * // Basic usage - Get phone numbers
 * const { data, loading, error, refetch } = useGoto({
 *   operation: 'getPhoneNumbers',
 *   variables: { locationId: 123 }
 * });
 *
 * @example
 * // Lazy query usage
 * const { fetch, data, loading } = useGoto({
 *   operation: 'getPhoneNumbers',
 *   lazy: true
 * });
 * // Later...
 * fetch({ variables: { locationId: 123 } });
 *
 * @example
 * // With callbacks
 * const { data, loading } = useGoto({
 *   operation: 'getCallExtensions',
 *   variables: { locationId: 123 },
 *   onCompleted: (data) => console.log('Data loaded:', data),
 *   onError: (error) => console.error('Error:', error)
 * });
 */
const useGoto = ({
  operation,
  variables = {},
  skip = false,
  fetchPolicy = "cache-first",
  lazy = false,
  onCompleted,
  onError,
} = {}) => {
  // ==================== QUERIES ====================

  /**
   * Get GoTo Phone Numbers
   * Query: GET_GOTO_PHONE_NUMBERS
   * Variables: { locationId!, departmentLevel?, search?, page?, pageSize? }
   */
  const useGetPhoneNumbers = () => {
    if (lazy) {
      const [fetch, { data, loading, error, refetch, called }] = useLazyQuery(
        GET_GOTO_PHONE_NUMBERS,
        {
          onCompleted,
          onError,
        }
      );
      return {
        fetch,
        data: data?.getCallPhoneNumber,
        loading,
        error,
        refetch,
        called,
      };
    }

    const { data, loading, error, refetch } = useQuery(GET_GOTO_PHONE_NUMBERS, {
      variables,
      skip,
      fetchPolicy,
      onCompleted,
      onError,
    });

    return {
      data: data?.getCallPhoneNumber,
      loading,
      error,
      refetch,
    };
  };

  /**
   * Get GoTo Call Extensions
   * Query: GET_GOTO_CALL_EXTENSIONS
   * Variables: { locationId!, departmentLevel?, search?, page?, pageSize? }
   */
  const useGetCallExtensions = () => {
    if (lazy) {
      const [fetch, { data, loading, error, refetch, called }] = useLazyQuery(
        GET_GOTO_CALL_EXTENSIONS,
        {
          onCompleted,
          onError,
        }
      );
      return {
        fetch,
        data: data?.getCallExtension,
        loading,
        error,
        refetch,
        called,
      };
    }

    const { data, loading, error, refetch } = useQuery(GET_GOTO_CALL_EXTENSIONS, {
      variables,
      skip,
      fetchPolicy,
      onCompleted,
      onError,
    });

    return {
      data: data?.getCallExtension,
      loading,
      error,
      refetch,
    };
  };

  /**
   * Get GoTo Users
   * Query: GET_GOTO_USERS
   * Variables: { locationId!, departmentLevel?, search?, page?, pageSize? }
   */
  const useGetUsers = () => {
    if (lazy) {
      const [fetch, { data, loading, error, refetch, called }] = useLazyQuery(GET_GOTO_USERS, {
        onCompleted,
        onError,
      });
      return {
        fetch,
        data: data?.getCallUser,
        loading,
        error,
        refetch,
        called,
      };
    }

    const { data, loading, error, refetch } = useQuery(GET_GOTO_USERS, {
      variables,
      skip,
      fetchPolicy,
      onCompleted,
      onError,
    });

    return {
      data: data?.getCallUser,
      loading,
      error,
      refetch,
    };
  };

  /**
   * Get GoTo Devices
   * Query: GET_GOTO_DEVICES
   * Variables: { locationId!, search?, page?, pageSize? }
   */
  const useGetDevices = () => {
    if (lazy) {
      const [fetch, { data, loading, error, refetch, called }] = useLazyQuery(GET_GOTO_DEVICES, {
        onCompleted,
        onError,
      });
      return {
        fetch,
        data: data?.getCallDevice,
        loading,
        error,
        refetch,
        called,
      };
    }

    const { data, loading, error, refetch } = useQuery(GET_GOTO_DEVICES, {
      variables,
      skip,
      fetchPolicy,
      onCompleted,
      onError,
    });

    return {
      data: data?.getCallDevice,
      loading,
      error,
      refetch,
    };
  };

  /**
   * Get GoTo Dashboard Summary
   * Query: GOTO_DASHBOARD_SUMMARY
   * Variables: { locationId! }
   */
  const useGetDashboardSummary = () => {
    if (lazy) {
      const [fetch, { data, loading, error, refetch, called }] = useLazyQuery(
        GOTO_DASHBOARD_SUMMARY,
        {
          onCompleted,
          onError,
        }
      );
      return {
        fetch,
        data: data?.getGotoDashboardSummary,
        loading,
        error,
        refetch,
        called,
      };
    }

    const { data, loading, error, refetch } = useQuery(GOTO_DASHBOARD_SUMMARY, {
      variables,
      skip,
      fetchPolicy,
      onCompleted,
      onError,
    });

    return {
      data: data?.getGotoDashboardSummary,
      loading,
      error,
      refetch,
    };
  };

  // ==================== MUTATIONS ====================

  /**
   * Update GoTo Call Extension
   * Mutation: UPDATE_GOTO_CALL_EXTENSION
   * Variables: { extensionId!, userId?, departmentId? }
   */
  const useUpdateCallExtension = () => {
    const [updateCallExtension, { data, loading, error }] = useMutation(
      UPDATE_GOTO_CALL_EXTENSION,
      {
        onCompleted,
        onError,
      }
    );
    return {
      updateCallExtension,
      data: data?.updateCallExtension,
      loading,
      error,
    };
  };

  /**
   * Update GoTo Phone Number
   * Mutation: UPDATE_GOTO_PHONE_NUMBER
   * Variables: { phoneNumberId!, userId?, departmentId? }
   */
  const useUpdatePhoneNumber = () => {
    const [updatePhoneNumber, { data, loading, error }] = useMutation(UPDATE_GOTO_PHONE_NUMBER, {
      onCompleted,
      onError,
    });
    return {
      updatePhoneNumber,
      data: data?.updateCallPhoneNumber,
      loading,
      error,
    };
  };

  /**
   * Update GoTo User
   * Mutation: UPDATE_GOTO_USER
   * Variables: { updateUserId!, userId?, departmentId? }
   */
  const useUpdateUser = () => {
    const [updateUser, { data, loading, error }] = useMutation(UPDATE_GOTO_USER, {
      onCompleted,
      onError,
    });
    return {
      updateUser,
      data: data?.updateCallUser,
      loading,
      error,
    };
  };

  /**
   * Update GoTo Call Device
   * Mutation: UPDATE_GOTO_CALL_DEVICE
   * Variables: { deviceId!, userId?, departmentId? }
   */
  const useUpdateCallDevice = () => {
    const [updateCallDevice, { data, loading, error }] = useMutation(UPDATE_GOTO_CALL_DEVICE, {
      onCompleted,
      onError,
    });
    return {
      updateCallDevice,
      data: data?.updateCallDevice,
      loading,
      error,
    };
  };

  /**
   * Sync GoTo Call Data
   * Mutation: SYNC_GOTO_CALL_DATA
   * Variables: { locationIds: [Int]! }
   */
  const useSyncGotoCallData = () => {
    const [syncGotoCallData, { data, loading, error }] = useMutation(SYNC_GOTO_CALL_DATA, {
      onCompleted,
      onError,
    });
    return {
      syncGotoCallData,
      data: data?.syncCallData,
      loading,
      error,
    };
  };

  // ==================== OPERATION ROUTER ====================

  /**
   * Route to the appropriate hook based on operation type
   */
  const operationMap = {
    // Queries
    getPhoneNumbers: useGetPhoneNumbers,
    getCallExtensions: useGetCallExtensions,
    getUsers: useGetUsers,
    getDevices: useGetDevices,
    getDashboardSummary: useGetDashboardSummary,

    // Mutations
    updateCallExtension: useUpdateCallExtension,
    updatePhoneNumber: useUpdatePhoneNumber,
    updateUser: useUpdateUser,
    updateCallDevice: useUpdateCallDevice,
    syncGotoCallData: useSyncGotoCallData,
  };

  // Execute the requested operation
  if (!operation) {
    throw new Error("useGoto: 'operation' parameter is required");
  }

  const operationHandler = operationMap[operation];

  if (!operationHandler) {
    throw new Error(
      `useGoto: Unknown operation '${operation}'. Available operations: ${Object.keys(
        operationMap
      ).join(", ")}`
    );
  }

  return operationHandler();
};

export default useGoto;
